/**
 * Tabelas de dados do tenant ("datasets").
 *
 * Proposital: NÃO reaproveita `EstruturaDados` (que tipa portas de fluxo e não
 * possui registros gravados). Um dataset tem linhas, então seu schema precisa de
 * chave estável, regras de evolução e flags que só fazem sentido com dados.
 */

export type TipoCampoDataset =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "datetime";

export const tiposCampoDataset: { id: TipoCampoDataset; label: string }[] = [
  { id: "string", label: "Texto" },
  { id: "number", label: "Número" },
  { id: "boolean", label: "Sim/Não" },
  { id: "date", label: "Data" },
  { id: "datetime", label: "Data e hora" },
];

export type FormatoCampoDataset = "texto" | "moeda" | "percentual";

export interface CampoDataset {
  /** Identidade estável do campo. Nunca muda: é a chave dentro de `dados`. */
  chave: string;
  /** Rótulo de exibição. Pode ser renomeado livremente. */
  rotulo: string;
  tipo: TipoCampoDataset;
  obrigatorio?: boolean;
  /** Cria índice de expressão no jsonb para este campo. */
  indexado?: boolean;
  /** Entra no texto usado para gerar o vetor de busca semântica. */
  buscavel?: boolean;
  formato?: FormatoCampoDataset;
  /** Quando preenchido, restringe os valores aceitos. */
  valores?: string[];
  /** Soft-delete do campo: o valor segue no jsonb, mas some do schema. */
  removido_em?: string;
}

export type OperadorFiltroDataset =
  | "eq"
  | "ne"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "contem"
  | "comeca"
  | "entre"
  | "em"
  | "vazio"
  | "naoVazio";

export interface FiltroDataset {
  campo: string;
  operador: OperadorFiltroDataset;
  /** Usado por todos os operadores, exceto `entre`, `em`, `vazio` e `naoVazio`. */
  valor?: string | number | boolean | null;
  /** `entre` espera 2 valores; `em` espera 1 ou mais. */
  valores?: (string | number | boolean)[];
}

export interface OrdemDataset {
  campo: string;
  direcao?: "asc" | "desc";
}

export interface ConsultaDataset {
  filtros?: FiltroDataset[];
  /** Busca semântica/textual livre, usada para achar o registro quando o
   * pedido é vago. Combina com `filtros`. */
  busca?: string;
  ordem?: OrdemDataset[];
  limite?: number;
}

export interface Dataset {
  id?: number;
  chave: string;
  nome: string;
  descricao?: string;
  campos: CampoDataset[];
  /** Chaves de campo que identificam o registro no upsert. Vazio = sempre insere. */
  campos_chave?: string[];
  /** Habilita busca semântica. Gerado a partir dos campos `buscavel`. */
  busca_semantica?: boolean;
}

/** Operadores aceitos por tipo de campo. Usado na validação do backend e na UI. */
export const operadoresPorTipo: Record<
  TipoCampoDataset,
  OperadorFiltroDataset[]
> = {
  string: ["eq", "ne", "contem", "comeca", "em", "vazio", "naoVazio"],
  number: ["eq", "ne", "gt", "gte", "lt", "lte", "entre", "em", "vazio", "naoVazio"],
  boolean: ["eq", "ne", "vazio", "naoVazio"],
  date: ["eq", "ne", "gt", "gte", "lt", "lte", "entre", "vazio", "naoVazio"],
  datetime: ["eq", "ne", "gt", "gte", "lt", "lte", "entre", "vazio", "naoVazio"],
};

/** Campos visíveis (não removidos) do dataset. */
export function camposAtivos(dataset: Pick<Dataset, "campos">): CampoDataset[] {
  return (dataset.campos ?? []).filter((c) => !c.removido_em);
}
